import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, InputLabel, Link, MenuItem, Select, TextField, Tooltip, Typography, useTheme, Button, IconButton } from "@mui/material";
// import RefreshIcon from "@mui/icons-material/Refresh"; // unused while the Refresh button below is disabled
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { tokens } from "../../theme";
import { MARKET_INDENTED_LIST } from "../../utilities/constants";
import { getLocalDateTime, getSortedMarket } from "../../utilities/helpers";
import { EqipItem } from "../../components/EqipItem";
import ModalDialog from "../../components/ModalDialog";
import codeTablePostgresApi from "../../apis/codeTablePostgresApi";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";

const AUTO_REFRESH_INTERVAL_MS = 30000;

// onedrivesyncruns timestamps come back with no timezone marker but are
// always in UTC (the server runs in UTC) - the browser's Date parser treats
// a marker-less string as already being in the viewer's local time, so
// without this it displays raw server time mislabeled as local.
const getLocalDateTimeFromUtc = (isoString) => {
  if (!isoString) return "";
  const hasTimezoneMarker = /Z$|[+-]\d{2}:?\d{2}$/.test(isoString);
  const date = new Date(hasTimezoneMarker ? isoString : `${isoString}Z`);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

// Mirrors scenes/aiv2 (Manage AI Document): no upload/drag-drop, since
// OneDrive is the system of record for new documents — but delete IS
// supported here, and deletes the source file in OneDrive too, as a manual
// fallback for when OneDrive-side deletion isn't an option.
const OneDriveDocuments = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [selectedCommodities, setSelectedCommodities] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [lastSyncRun, setLastSyncRun] = useState(null);
  const [lastSyncDismissed, setLastSyncDismissed] = useState(false);
  const [showSyncFileDetails, setShowSyncFileDetails] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isResettingSync, setIsResettingSync] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openDeleteSelectedModal, setOpenDeleteSelectedModal] = useState(false);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);
  const [openDeleteAllModal, setOpenDeleteAllModal] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState("");
  const [priceDialogMode, setPriceDialogMode] = useState(null); // "row" | "selected" | "bulk" | null
  const [priceDialogRow, setPriceDialogRow] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [isSavingPrice, setIsSavingPrice] = useState(false);
  const [ratePlans, setRatePlans] = useState([]); // one row per catalog plan, for the selected commodity/market
  const [ratePlanInputs, setRatePlanInputs] = useState({}); // pricing_plan_id -> text field value
  const [isLoadingRatePlans, setIsLoadingRatePlans] = useState(false);
  const [savingPlanId, setSavingPlanId] = useState(null);
  const [resettingPlanId, setResettingPlanId] = useState(null);

  const allowedCommodities = useSelector((state) => state.allowedCommodities);
  const allowedMarkets = useSelector((state) => state.allowedMarkets);

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      let url = `/api/onedrivedocuments?page=${page + 1}&page_size=${pageSize}`;
      if (searchText) {
        url += `&original_filename=${searchText}`;
      }
      if (selectedCommodities?.length > 0) {
        url += `&commodity=${selectedCommodities[0]}`;
      }
      if (selectedMarkets?.length > 0) {
        url += `&market=${selectedMarkets[0]}`;
      }
      const { data } = await ieqipCoreServiceApi.get(url);
      setDocuments(data.documents || []);
      setTotalCount(data.total_count || 0);
      setErrorMessage(null);
    } catch (error) {
      console.error("fetchDocuments", error);
      setErrorMessage(`Failed to load OneDrive documents: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLastSyncRun = async () => {
    try {
      const { data } = await ieqipCoreServiceApi.get(`/api/onedrivesyncruns?page=1&page_size=1`);
      const run = data.items?.[0] || null;
      setLastSyncRun((previous) => {
        if (!previous || run?.onedrive_sync_run_uid !== previous.onedrive_sync_run_uid) {
          setLastSyncDismissed(false);
        }
        return run;
      });
      return run;
    } catch (error) {
      console.error("fetchLastSyncRun", error);
      return null;
    }
  };

  // "Sync Now" always syncs exactly one Commodity+Market - the one currently
  // selected - never the whole account (spec 005-manual-onedrive-sync
  // FR-003). Mirrors the existing selectedCommodities[0]/selectedMarkets[0]
  // single-selection pattern already used by "Set Price for All"/"Delete All"
  // on this same page.
  const canSyncNow = selectedCommodities.length === 1 && selectedMarkets.length === 1;

  // Derived from the polled server-side run status (not just local isSyncing
  // state), so the button stays correctly blocked across a page reload or a
  // run started in another tab/by another user (spec 005 FR-008).
  const isSyncRunning = isSyncing || lastSyncRun?.status === "running";

  const handleSyncNow = async () => {
    if (!canSyncNow || isSyncRunning) return;
    setIsSyncing(true);
    setSyncMessage(null);
    try {
      // The endpoint runs the sync inline and returns once it's done; it
      // responds with 502 (not 200) if the run itself failed, so a 2xx alone
      // isn't "it worked" — a 2xx can also mean "skipped" (another run was
      // already in progress), which is not the same thing as completed.
      const { data } = await ieqipCoreServiceApi.post(
        "/api/onedrivesync/run",
        null,
        { params: { commodity: selectedCommodities[0], market: selectedMarkets[0] }, timeout: 300000 }
      );
      await Promise.all([fetchLastSyncRun(), fetchDocuments()]);
      if (data.status === "skipped") {
        setSyncMessage({ severity: "warning", text: `Sync skipped — ${data.reason || "another run is already in progress"}.` });
      } else if (data.files_failed) {
        setSyncMessage({
          severity: "warning",
          text: `Sync completed with errors — added ${data.files_added ?? 0}, updated ${data.files_updated ?? 0}, removed ${data.files_removed ?? 0}, failed ${data.files_failed}.` +
            (data.error_message ? ` Last error: ${data.error_message}` : ""),
        });
      } else {
        setSyncMessage({
          severity: "success",
          text: `Sync completed — added ${data.files_added ?? 0}, updated ${data.files_updated ?? 0}, removed ${data.files_removed ?? 0}.`,
        });
      }
    } catch (error) {
      console.error("handleSyncNow", error);
      const detail = error.response?.data?.detail;
      setSyncMessage({ severity: "error", text: detail ? `Sync failed: ${detail}` : `Sync request failed: ${error.message}` });
      // The failed run was still recorded server-side — refresh the banner.
      fetchLastSyncRun();
    } finally {
      setIsSyncing(false);
    }
  };

  // There is no automatic stale-run reclaim by design (a genuinely stuck run
  // is a job for a human, not a timer) - this is that human's escape hatch,
  // available without needing direct database access. Already-ingested files
  // are already tracked, so the next Sync Now naturally resumes where a
  // reset run left off - nothing needs to be replayed.
  const handleResetStuckSync = async () => {
    setIsResettingSync(true);
    try {
      await ieqipCoreServiceApi.post("/api/onedrivesync/reset");
      await fetchLastSyncRun();
      setSyncMessage({ severity: "warning", text: "Sync was reset. Press Sync Now to try again." });
    } catch (error) {
      console.error("handleResetStuckSync", error);
      setSyncMessage({ severity: "error", text: `Reset failed: ${error.message}` });
    } finally {
      setIsResettingSync(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const { data } = await codeTablePostgresApi.get(`/codetables/codetabletypes/12,13`);
      const codeTables = data.body;
      setCommodities(
        codeTables
          .filter((codeTable) => codeTable.code_table_type_id === 12 && allowedCommodities.includes(codeTable.code))
          .sort((a, b) => a.description.localeCompare(b.description))
      );
      setMarkets(getSortedMarket(codeTables, allowedMarkets));
    } catch (error) {
      console.error("fetchFilters", error);
    }
  };

  const fetchRatePlans = async (commodity, market) => {
    setIsLoadingRatePlans(true);
    try {
      const { data } = await ieqipCoreServiceApi.get("/api/commoditymarketplanprice", { params: { commodity, market } });
      setRatePlans(data || []);
      const inputs = {};
      (data || []).forEach((plan) => {
        inputs[plan.pricing_plan_id] = String(plan.price);
      });
      setRatePlanInputs(inputs);
    } catch (error) {
      console.error("fetchRatePlans", error);
    } finally {
      setIsLoadingRatePlans(false);
    }
  };

  const handleSaveRatePlan = async (pricingPlanId) => {
    const input = ratePlanInputs[pricingPlanId];
    const price = Number(input);
    if (input === "" || input == null || !Number.isFinite(price) || price < 0) {
      setErrorMessage("Enter a valid non-negative price.");
      return;
    }
    setSavingPlanId(pricingPlanId);
    setErrorMessage(null);
    try {
      const { data } = await ieqipCoreServiceApi.put(
        "/api/commoditymarketplanprice",
        { price },
        { params: { commodity: selectedCommodities[0], market: selectedMarkets[0], pricing_plan_id: pricingPlanId } }
      );
      setRatePlans((previous) => previous.map((plan) => (plan.pricing_plan_id === pricingPlanId ? data : plan)));
    } catch (error) {
      console.error("handleSaveRatePlan", error);
      setErrorMessage(`Failed to set price. ${error.response?.data?.detail || error.message}`);
    } finally {
      setSavingPlanId(null);
    }
  };

  const handleResetRatePlan = async (pricingPlanId) => {
    setResettingPlanId(pricingPlanId);
    setErrorMessage(null);
    try {
      await ieqipCoreServiceApi.delete("/api/commoditymarketplanprice", {
        params: { commodity: selectedCommodities[0], market: selectedMarkets[0], pricing_plan_id: pricingPlanId },
      });
      await fetchRatePlans(selectedCommodities[0], selectedMarkets[0]);
    } catch (error) {
      console.error("handleResetRatePlan", error);
      setErrorMessage(`Failed to reset price. ${error.response?.data?.detail || error.message}`);
    } finally {
      setResettingPlanId(null);
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchLastSyncRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCommodities.length > 0 && selectedMarkets.length > 0) {
      fetchRatePlans(selectedCommodities[0], selectedMarkets[0]);
    } else {
      setRatePlans([]);
      setRatePlanInputs({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommodities, selectedMarkets]);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedCommodities, selectedMarkets]);

  // Auto-refresh so newly-synced and removed files show up without a manual
  // click — the actual sync runs server-side on its own schedule regardless
  // of this page being open; this just polls the current state more often
  // than someone would realistically hit Refresh themselves. Refs (rather
  // than a dependency array) keep a single interval alive for the life of
  // the page while always calling the latest closures, so a filter change
  // doesn't need to tear down and restart the poll.
  const fetchDocumentsRef = useRef(fetchDocuments);
  fetchDocumentsRef.current = fetchDocuments;
  const fetchLastSyncRunRef = useRef(fetchLastSyncRun);
  fetchLastSyncRunRef.current = fetchLastSyncRun;

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchDocumentsRef.current();
      fetchLastSyncRunRef.current();
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  const handleCommodityChange = (event) => {
    const value = event.target.value;
    setSelectedCommodities(typeof value === "string" ? value.split(",") : value.slice(-1));
    setPage(0);
  };

  const handleMarketChange = (event) => {
    const value = event.target.value;
    setSelectedMarkets(typeof value === "string" ? value.split(",") : value.slice(-1));
    setPage(0);
  };

  const handleDownload = async (document_) => {
    try {
      const bucketName = process.env.REACT_APP_S3_IEQIP_AI_DOCUMENT;
      const folder = `${document_.commodity}_${document_.market}_${process.env.REACT_APP_ENVIRONMENT}`;
      const response = await ieqipCoreServiceApi.get(
        `/api/s3/v2/download/${bucketName}/${folder}/${document_.uploaded_filename}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", document_.original_filename || document_.uploaded_filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const handleDeleteSelectedOk = async () => {
    if (selectedRows.length === 0) return;
    setIsDeletingSelected(true);
    setErrorMessage(null);
    try {
      const { data } = await ieqipCoreServiceApi.post(
        "/api/onedrivedocuments/batch/delete",
        { document_uids: selectedRows },
        { timeout: 120000 }
      );
      if (data?.errors?.length) {
        setErrorMessage(
          `Some documents could not be deleted:\n${data.errors.map((e) => `${e.document_uid}: ${e.error}`).join("\n")}`
        );
      }
      setSelectedRows([]);
      await fetchDocuments();
    } catch (error) {
      console.error("handleDeleteSelectedOk", error);
      setErrorMessage(`Failed to delete selected documents. ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsDeletingSelected(false);
      setOpenDeleteSelectedModal(false);
    }
  };

  const handleDeleteAllOk = async () => {
    setIsDeletingAll(true);
    setErrorMessage(null);
    try {
      const { data } = await ieqipCoreServiceApi.delete("/api/onedrivedocuments/bulk", {
        params: { commodity: selectedCommodities[0], market: selectedMarkets[0] },
        timeout: 120000,
      });
      if (data?.errors?.length) {
        setErrorMessage(
          `Some documents could not be deleted:\n${data.errors.map((e) => `${e.document_uid}: ${e.error}`).join("\n")}`
        );
      }
      await fetchDocuments();
    } catch (error) {
      console.error("handleDeleteAllOk", error);
      setErrorMessage(`Failed to delete all documents. ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsDeletingAll(false);
      setOpenDeleteAllModal(false);
      setDeleteAllConfirmText("");
    }
  };

  const openPriceDialog = (mode, row = null) => {
    setPriceDialogMode(mode);
    setPriceDialogRow(row);
    setPriceInput(row?.price != null ? String(row.price) : "");
  };

  const closePriceDialog = () => {
    setPriceDialogMode(null);
    setPriceDialogRow(null);
    setPriceInput("");
  };

  const handleSavePrice = async () => {
    const price = Number(priceInput);
    if (priceInput === "" || !Number.isFinite(price) || price < 0) {
      setErrorMessage("Enter a valid non-negative price.");
      return;
    }
    setIsSavingPrice(true);
    setErrorMessage(null);
    try {
      if (priceDialogMode === "row") {
        await ieqipCoreServiceApi.patch(`/api/onedrivedocuments/${priceDialogRow.ai_assistant_document_uid}/price`, { price });
      } else if (priceDialogMode === "selected") {
        const { data } = await ieqipCoreServiceApi.post("/api/onedrivedocuments/batch/price", {
          document_uids: selectedRows,
          price,
        });
        if (data?.errors?.length) {
          setErrorMessage(
            `Some documents could not be priced:\n${data.errors.map((e) => `${e.document_uid}: ${e.error}`).join("\n")}`
          );
        }
        setSelectedRows([]);
      } else if (priceDialogMode === "bulk") {
        await ieqipCoreServiceApi.patch(
          "/api/onedrivedocuments/bulk/price",
          { price },
          { params: { commodity: selectedCommodities[0], market: selectedMarkets[0] } }
        );
      }
      await fetchDocuments();
      closePriceDialog();
    } catch (error) {
      console.error("handleSavePrice", error);
      setErrorMessage(`Failed to set price. ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsSavingPrice(false);
    }
  };

  const getExtractionStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    {
      field: "original_filename",
      headerName: "File Name",
      flex: 4,
      minWidth: 300,
      renderCell: ({ row }) => <Tooltip title={row.original_filename}>{row.original_filename}</Tooltip>,
    },
    { field: "commodity", headerName: "Commodity", flex: 1, minWidth: 100 },
    { field: "market", headerName: "Market", flex: 1, minWidth: 100 },
    {
      field: "sync_status",
      headerName: "Sync Status",
      flex: 1,
      minWidth: 140,
      renderCell: ({ row }) =>
        row.sync_status === "failed" ? (
          <Tooltip title={row.sync_error_message || "Sync failed"}>
            <Chip label="FAILED" color="error" size="small" />
          </Tooltip>
        ) : (
          <Chip label="SYNCED" color="success" size="small" variant="outlined" />
        ),
    },
    {
      field: "extraction_status",
      headerName: "Extraction Status",
      flex: 1,
      minWidth: 140,
      renderCell: ({ row }) =>
        row.extraction_status ? (
          <Chip label={row.extraction_status.toUpperCase()} color={getExtractionStatusColor(row.extraction_status)} size="small" />
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        ),
    },
    {
      field: "price",
      headerName: "Price",
      flex: 1,
      minWidth: 130,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2">{row.price != null ? `$${Number(row.price).toFixed(2)}` : "-"}</Typography>
          <IconButton size="small" onClick={() => openPriceDialog("row", row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: "created_date",
      headerName: "Synced Date",
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => <>{getLocalDateTime(row.created_date)}</>,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 100,
      renderCell: ({ row }) =>
        row.sync_status === "failed" ? (
          <Tooltip title="This file failed to sync — nothing to download yet">
            <span>
              <IconButton color="success" disabled>
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <IconButton color="success" onClick={() => handleDownload(row)}>
            <FileDownloadIcon />
          </IconButton>
        ),
    },
  ];

  return (
    <Box m="20px">
      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ whiteSpace: "pre-line", mb: 1 }}>
          {errorMessage}
        </Alert>
      )}
      {syncMessage && (
        <Alert severity={syncMessage.severity} onClose={() => setSyncMessage(null)} sx={{ mb: 1 }}>
          {syncMessage.text}
        </Alert>
      )}
      {lastSyncRun && !lastSyncDismissed && (
        <Alert
          severity={lastSyncRun.status === "failed" ? "error" : lastSyncRun.files_failed ? "warning" : "info"}
          onClose={() => setLastSyncDismissed(true)}
          sx={{ mb: 1, whiteSpace: "pre-line" }}
        >
          Last sync: {getLocalDateTimeFromUtc(lastSyncRun.completed_date || lastSyncRun.started_date)} — {lastSyncRun.status}
          {lastSyncRun.status === "running" &&
            lastSyncRun.current_commodity &&
            lastSyncRun.current_market &&
            ` — processing ${lastSyncRun.current_commodity} / ${lastSyncRun.current_market}`}
          {lastSyncRun.status === "running" &&
            lastSyncRun.current_file &&
            ` — currently syncing: ${lastSyncRun.current_file}`}
          {lastSyncRun.status !== "failed" &&
            ` (added ${lastSyncRun.files_added}, updated ${lastSyncRun.files_updated}, removed ${lastSyncRun.files_removed}${
              lastSyncRun.files_failed ? `, failed ${lastSyncRun.files_failed}` : ""
            })`}
          {!!lastSyncRun.files_failed && lastSyncRun.error_message && `\nLast error: ${lastSyncRun.error_message}`}
          {lastSyncRun.file_changes &&
            (lastSyncRun.file_changes.added?.length > 0 ||
              lastSyncRun.file_changes.updated?.length > 0 ||
              lastSyncRun.file_changes.removed?.length > 0 ||
              lastSyncRun.file_changes.failed?.length > 0) && (
              <Box sx={{ mt: 0.5 }}>
                <Link component="button" variant="body2" onClick={() => setShowSyncFileDetails((prev) => !prev)}>
                  {showSyncFileDetails ? "Hide file details" : "Show file details"}
                </Link>
                <Collapse in={showSyncFileDetails}>
                  <Box sx={{ mt: 1 }}>
                    {lastSyncRun.file_changes.added?.length > 0 && (
                      <Typography variant="body2">
                        <strong>Added:</strong> {lastSyncRun.file_changes.added.join(", ")}
                      </Typography>
                    )}
                    {lastSyncRun.file_changes.updated?.length > 0 && (
                      <Typography variant="body2">
                        <strong>Updated:</strong> {lastSyncRun.file_changes.updated.join(", ")}
                      </Typography>
                    )}
                    {lastSyncRun.file_changes.removed?.length > 0 && (
                      <Typography variant="body2">
                        <strong>Removed:</strong> {lastSyncRun.file_changes.removed.join(", ")}
                      </Typography>
                    )}
                    {lastSyncRun.file_changes.failed?.length > 0 && (
                      <Typography variant="body2" color="error">
                        <strong>Failed:</strong>
                        <ul style={{ margin: 0 }}>
                          {lastSyncRun.file_changes.failed.map((entry, index) => (
                            <li key={index}>{entry}</li>
                          ))}
                        </ul>
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}
        </Alert>
      )}
      <Box>
        <FormControl sx={{ minWidth: 300, marginTop: 1, marginBottom: 1, marginRight: 1 }}>
          <InputLabel id="onedrive-commodity-label">Commodity</InputLabel>
          <Select
            fullWidth
            labelId="onedrive-commodity-label"
            label="Commodity"
            value={selectedCommodities}
            onChange={handleCommodityChange}
            renderValue={(selected) => selected.join(", ")}
          >
            {commodities.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                <Checkbox checked={selectedCommodities.indexOf(option.code) > -1} />
                {option.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 300, marginTop: 1, marginBottom: 1 }}>
          <InputLabel id="onedrive-market-label">Market</InputLabel>
          <Select
            fullWidth
            labelId="onedrive-market-label"
            label="Market"
            value={selectedMarkets}
            onChange={handleMarketChange}
            renderValue={(selected) => selected.join(", ")}
          >
            {markets.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {MARKET_INDENTED_LIST.includes(option.code) && <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>}
                <Checkbox checked={selectedMarkets.indexOf(option.code) > -1} />
                {option.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {selectedCommodities.length > 0 && selectedMarkets.length > 0 && (
        <Box sx={{ marginTop: 1, marginBottom: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Pricing plans for {selectedCommodities[0]} / {selectedMarkets[0]}:
          </Typography>
          {isLoadingRatePlans ? (
            <CircularProgress size={16} />
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {ratePlans.map((plan) => (
                <Box key={plan.pricing_plan_id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" sx={{ minWidth: 110 }}>
                    {plan.pricing_plan_name}:
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    value={ratePlanInputs[plan.pricing_plan_id] ?? ""}
                    onChange={(e) =>
                      setRatePlanInputs((previous) => ({ ...previous, [plan.pricing_plan_id]: e.target.value }))
                    }
                    disabled={savingPlanId === plan.pricing_plan_id}
                    sx={{ width: 110 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleSaveRatePlan(plan.pricing_plan_id)}
                    disabled={savingPlanId === plan.pricing_plan_id}
                  >
                    {savingPlanId === plan.pricing_plan_id ? <CircularProgress size={16} /> : <EditIcon fontSize="small" />}
                  </IconButton>
                  {plan.is_custom && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => handleResetRatePlan(plan.pricing_plan_id)}
                      disabled={resettingPlanId === plan.pricing_plan_id}
                    >
                      {resettingPlanId === plan.pricing_plan_id ? "Resetting…" : "Reset"}
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
      <Box>
        <FormControl fullWidth sx={{ marginTop: 1, marginBottom: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={11}>
              <EqipItem>
                <TextField fullWidth label="Enter file name" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
              </EqipItem>
            </Grid>
            <Grid item xs={1}>
              <Button variant="contained" onClick={fetchDocuments}>
                Search
              </Button>
            </Grid>
          </Grid>
        </FormControl>
      </Box>
      <Box sx={{ marginTop: 1, marginBottom: 1, display: "flex", alignItems: "center", gap: 1 }}>
        {/* <Button variant="outlined" color="secondary" startIcon={<RefreshIcon />} onClick={fetchDocuments} disabled={isLoading}>
          Refresh
        </Button> */}
        <Typography variant="caption" color="text.secondary">
          Auto-refreshing every {AUTO_REFRESH_INTERVAL_MS / 1000}s
        </Typography>
        <Tooltip
          title={
            !canSyncNow
              ? "Select exactly one Commodity and one Market to sync"
              : isSyncRunning
              ? "A sync is already in progress"
              : ""
          }
        >
          <span>
            <Button
              variant="contained"
              color="secondary"
              startIcon={isSyncRunning ? <CircularProgress size={16} color="inherit" /> : <CloudSyncIcon />}
              onClick={handleSyncNow}
              disabled={isSyncRunning || !canSyncNow}
            >
              {isSyncRunning ? "Syncing…" : "Sync Now"}
            </Button>
          </span>
        </Tooltip>
        {lastSyncRun?.status === "running" && (
          <Tooltip title="Use this if a sync appears stuck and isn't making progress. It won't lose work — files already synced stay synced, and the next Sync Now picks up where this one left off.">
            <Button
              variant="outlined"
              color="warning"
              onClick={handleResetStuckSync}
              disabled={isResettingSync}
            >
              {isResettingSync ? "Resetting…" : "Reset stuck sync"}
            </Button>
          </Tooltip>
        )}
        {selectedRows.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AttachMoneyIcon />}
            onClick={() => openPriceDialog("selected")}
          >
            Set Price ({selectedRows.length})
          </Button>
        )}
        {selectedRows.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setOpenDeleteSelectedModal(true)}
            disabled={isDeletingSelected}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        )}
        {selectedCommodities.length > 0 && selectedMarkets.length > 0 && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AttachMoneyIcon />}
            onClick={() => openPriceDialog("bulk")}
          >
            Set Price for All
          </Button>
        )}
        {selectedCommodities.length > 0 && selectedMarkets.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setOpenDeleteAllModal(true)}
            disabled={isLoading || isDeletingAll}
          >
            Delete All
          </Button>
        )}
      </Box>
      <Box
        m="8px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
        }}
      >
        <DataGrid
          rows={documents}
          columns={columns}
          loading={isLoading}
          pagination
          pageSize={pageSize}
          rowsPerPageOptions={[pageSize]}
          rowCount={totalCount}
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          getRowId={(row) => row.ai_assistant_document_uid}
          checkboxSelection
          onSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          selectionModel={selectedRows}
        />
      </Box>
      <ModalDialog
        title="Delete Selected"
        content={`Are you sure you want to delete ${selectedRows.length} selected document(s)? This will remove the file from OneDrive (if it still exists there), the vector store, S3, and the database.`}
        isLoading={isDeletingSelected}
        open={openDeleteSelectedModal}
        handleOk={handleDeleteSelectedOk}
        handleCancel={() => setOpenDeleteSelectedModal(false)}
      />
      <Dialog open={openDeleteAllModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={600}>Delete All Documents</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Typography>
            Are you sure you want to delete <strong>all</strong> OneDrive-synced documents for{" "}
            <strong>{selectedCommodities[0]}</strong> / <strong>{selectedMarkets[0]}</strong>?
            This removes each file from OneDrive (if it still exists there), the vector store, S3,
            and the database. This cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            To confirm, type <strong>{selectedCommodities[0]} / {selectedMarkets[0]}</strong> below:
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={`${selectedCommodities[0]} / ${selectedMarkets[0]}`}
            value={deleteAllConfirmText}
            onChange={(e) => setDeleteAllConfirmText(e.target.value)}
            disabled={isDeletingAll}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button
            onClick={() => { setOpenDeleteAllModal(false); setDeleteAllConfirmText(""); }}
            color="inherit"
            variant="outlined"
            disabled={isDeletingAll}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllOk}
            variant="contained"
            color="error"
            disabled={isDeletingAll || deleteAllConfirmText !== `${selectedCommodities[0]} / ${selectedMarkets[0]}`}
            startIcon={isDeletingAll ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isDeletingAll ? "Deleting…" : "Delete All"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={priceDialogMode !== null} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={600}>Set Price</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Typography sx={{ mb: 2 }}>
            {priceDialogMode === "row" && `Set the price for "${priceDialogRow?.original_filename}".`}
            {priceDialogMode === "selected" &&
              `Set one price for the ${selectedRows.length} selected document(s). This overwrites any existing price on each.`}
            {priceDialogMode === "bulk" &&
              `Set one price for every document in ${selectedCommodities[0]} / ${selectedMarkets[0]}. This overwrites any existing price, including documents already individually priced.`}
          </Typography>
          <TextField
            fullWidth
            autoFocus
            size="small"
            type="number"
            label="Price"
            inputProps={{ min: 0, step: "0.01" }}
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
            disabled={isSavingPrice}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button onClick={closePriceDialog} color="inherit" variant="outlined" disabled={isSavingPrice}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePrice}
            variant="contained"
            color="primary"
            disabled={isSavingPrice || priceInput === ""}
            startIcon={isSavingPrice ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSavingPrice ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OneDriveDocuments;
